import { type InUseHolder } from "~/components/usedBy/inUseSentence";
import { type RecordValidationErrorFragment } from "~/gql/CommonObjects.generated";
import { RecordKind } from "~/gql/graphqlBase.generated";
import { type UsedByGroupFieldsFragment } from "~/gql/queries/usedBy.generated";
import { buildCategoryDeepLinkRoute } from "~fulfill/product/categories/helpers/categoryDeepLink";
import { buildHardgoodDeepLinkRoute } from "~fulfill/product/hardgoods/helpers/hardgoodDeepLink";
import { buildItemsRoute } from "~fulfill/product/items/helpers/itemsRoutes";
import { buildPriceGroupPath } from "~fulfill/sales/pricing/helpers/priceGroupContinuation";

export interface UsedBySubject {
	holders?: readonly InUseHolder[];
	id: string;
	kind: RecordKind;
	name: string;
}

export type UsedByRef = UsedByGroupFieldsFragment["refs"][number];

export type UsedByGroup = UsedByGroupFieldsFragment;

export type RecordErrorFields = {
	[K in keyof RecordValidationErrorFragment]?: null | RecordValidationErrorFragment[K];
};

export const USED_BY_PANEL_KINDS = [RecordKind.Category, RecordKind.CustomerType, RecordKind.PriceGroup] as const;

export const canOpenUsedBy = (kind: RecordKind): boolean =>
	(USED_BY_PANEL_KINDS as readonly RecordKind[]).includes(kind);

const CUSTOMERS_PATH = "/fulfill/customers/customer";

const findRefusedRecord = (
	errors: null | ReadonlyArray<RecordErrorFields> | undefined,
	name: string
): UsedBySubject | null => {
	const refused = errors?.find(
		(error) => error.recordId && error.recordKind && error.recordKind !== RecordKind.Unknown
	);
	if (!refused?.recordId || !refused.recordKind) return null;
	const holders = refused.inUseBreakdown?.length ? refused.inUseBreakdown : undefined;
	return { id: refused.recordId, kind: refused.recordKind, name, ...(holders ? { holders } : {}) };
};

const buildRouteForKind = (kind: RecordKind, id: string): null | string => {
	switch (kind) {
		case RecordKind.Category:
			return buildCategoryDeepLinkRoute(id);
		case RecordKind.Customer:
			return `${CUSTOMERS_PATH}/${encodeURIComponent(id)}`;
		case RecordKind.HardGood:
			return buildHardgoodDeepLinkRoute(id);
		case RecordKind.Item:
			return buildItemsRoute({ itemId: encodeURIComponent(id), mode: "detail" });
		case RecordKind.PriceGroup:
			return buildPriceGroupPath(id);
		default:
			return null;
	}
};

export const buildUsedByRefRoute = (ref: Pick<UsedByRef, "id" | "kind" | "parent">): null | string =>
	ref.parent ? buildRouteForKind(ref.parent.kind, ref.parent.id) : buildRouteForKind(ref.kind, ref.id);

export interface MorePage {
	hasMore: boolean;
	refs: UsedByRef[];
}

export const mergeUsedByGroups = (
	groups: readonly UsedByGroup[],
	morePages: Partial<Record<RecordKind, MorePage>>
): UsedByGroup[] =>
	groups.map((group) => {
		const more = morePages[group.kind];
		return more ? { ...group, hasMore: more.hasMore, refs: [...group.refs, ...more.refs] } : group;
	});

export default findRefusedRecord;
